import type { FileUploadFile, FileValidationError } from '@zealot/ui/components/file-upload';
import * as React from 'react';

type UploadAdapterResult = {
  url: string;
  key?: string;
};

type UploadAdapter = {
  upload: (
    file: File,
    options: {
      onProgress: (progress: number) => void;
      signal: AbortSignal;
    },
  ) => Promise<UploadAdapterResult>;
};

type UseFileUploadOptions = {
  adapter: UploadAdapter;
  onUploadComplete?: (file: FileUploadFile) => void;
  onUploadError?: (file: FileUploadFile, error: string) => void;
};

type UseFileUploadReturn = {
  files: FileUploadFile[];
  handleFilesChange: (files: FileUploadFile[]) => void;
  handleValidationError: (errors: FileValidationError[]) => void;
  isUploading: boolean;
  clearFiles: () => void;
  abortUpload: (fileId: string) => void;
  uploadedUrls: string[];
  validationError: string;
};

// gloss
const useFileUpload = ({ adapter, onUploadComplete, onUploadError }: UseFileUploadOptions): UseFileUploadReturn => {
  const [files, setFiles] = React.useState<FileUploadFile[]>([]);
  const [validationError, setValidationError] = React.useState('');
  const abortControllersRef = React.useRef<Map<string, AbortController>>(new Map());
  const filesRef = React.useRef(files);
  filesRef.current = files;

  const onUploadCompleteRef = React.useRef(onUploadComplete);
  onUploadCompleteRef.current = onUploadComplete;
  const onUploadErrorRef = React.useRef(onUploadError);
  onUploadErrorRef.current = onUploadError;

  const startUpload = React.useCallback(
    (fileEntry: FileUploadFile) => {
      const controller = new AbortController();
      abortControllersRef.current.set(fileEntry.id, controller);

      adapter
        .upload(fileEntry.file, {
          onProgress: (progress) => {
            setFiles((prev) => prev.map((f) => (f.id === fileEntry.id ? { ...f, progress } : f)));
          },
          signal: controller.signal,
        })
        .then((result) => {
          abortControllersRef.current.delete(fileEntry.id);

          const completed = {
            ...fileEntry,
            progress: 100,
            url: result.url,
            key: result.key,
          };

          setFiles((prev) =>
            prev.map((f) => (f.id === fileEntry.id ? { ...f, progress: 100, url: result.url, key: result.key } : f)),
          );

          onUploadCompleteRef.current?.(completed);
        })
        .catch((err) => {
          abortControllersRef.current.delete(fileEntry.id);
          if (controller.signal.aborted) return;

          const message = err instanceof Error ? err.message : 'Upload failed';
          const failed = { ...fileEntry, error: message, progress: undefined };

          setFiles((prev) =>
            prev.map((f) => (f.id === fileEntry.id ? { ...f, error: message, progress: undefined } : f)),
          );
          onUploadErrorRef.current?.(failed, message);
        });
    },
    [adapter],
  );

  const handleFilesChange = React.useCallback(
    (newFiles: FileUploadFile[]) => {
      setValidationError('');

      const oldIds = new Set(filesRef.current.map((f) => f.id));
      const newIds = new Set(newFiles.map((f) => f.id));

      for (const oldId of oldIds) {
        if (!newIds.has(oldId)) {
          const controller = abortControllersRef.current.get(oldId);
          if (controller) {
            controller.abort();
            abortControllersRef.current.delete(oldId);
          }
        }
      }

      const addedIds = new Set(newFiles.filter((f) => !oldIds.has(f.id)).map((f) => f.id));

      const filesWithProgress = newFiles.map((f) => (addedIds.has(f.id) ? { ...f, progress: 0 } : f));
      setFiles(filesWithProgress);

      for (const file of filesWithProgress) {
        if (addedIds.has(file.id)) {
          startUpload(file);
        }
      }
    },
    [startUpload],
  );

  const handleValidationError = React.useCallback((errors: FileValidationError[]) => {
    setValidationError(errors.map((e) => e.message).join(', '));
  }, []);

  const clearFiles = React.useCallback(() => {
    for (const controller of abortControllersRef.current.values()) {
      controller.abort();
    }
    abortControllersRef.current.clear();
    setFiles([]);
    setValidationError('');
  }, []);

  const abortUpload = React.useCallback((fileId: string) => {
    const controller = abortControllersRef.current.get(fileId);
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(fileId);
    }
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  React.useEffect(() => {
    return () => {
      for (const controller of abortControllersRef.current.values()) {
        controller.abort();
      }
      abortControllersRef.current.clear();
    };
  }, []);

  const isUploading = files.some((f) => f.progress !== undefined && f.progress < 100 && !f.error);
  const urlsKey = files
    .filter((f) => f.url)
    .map((f) => f.url!)
    .join('\0');
  const uploadedUrls = React.useMemo(() => urlsKey.split('\0').filter(Boolean), [urlsKey]);

  return {
    files,
    handleFilesChange,
    handleValidationError,
    isUploading,
    clearFiles,
    abortUpload,
    uploadedUrls,
    validationError,
  };
};

export type { UploadAdapter, UploadAdapterResult, UseFileUploadOptions, UseFileUploadReturn };
export { useFileUpload };
